import React, { useState } from "react"
import { ButtonFieldsFragment } from "../../../graphql-types"
import * as styles from "./button.module.scss"
import { graphql } from "gatsby"
import { ReactSVG } from "react-svg"
import { configureSVGAccessibility } from "../../utils/accessibility"

type RenderProps = {
  data: ButtonFieldsFragment
}

const getHref = (internalLink, externalLink) => {
  if (internalLink?.[0]) {
    return internalLink[0].__typename == "DatoCmsPage" ? `/${internalLink[0].slug}` : "/"
  }
  if (externalLink) {
    return externalLink
  }
  return "#"
}

const getTarget = (internalLink, externalLink) => {
  if (internalLink?.[0]) {
    return "_self"
  }
  if (externalLink) {
    return "_blank"
  }
  return "_self"
}

const Button: React.FC<RenderProps> = ({ data }) => {
  const [hoverButton, setHoverButton] = useState(false)

  const href = getHref(data.internalPageLink, data.externalPageLink)
  const target = getTarget(data.internalPageLink, data.externalPageLink)
  const rel = target === "_blank" ? "noopener noreferrer" : undefined

  const displayPictogram = (pictogram, color, color2, hover) => {
    return (
      <div className={styles.content}>
        {pictogram.format === "svg" ? (
          <ReactSVG
            className={styles.pictogram}
            src={pictogram.url}
            aria-hidden={true}
            beforeInjection={svg => {
              svg.querySelector("path")?.setAttribute("fill", hover ? color.hex : color2.hex)
              configureSVGAccessibility(svg, pictogram.alt)
            }}
          />
        ) : (
          <img src={pictogram.url} alt={pictogram.alt || ""} className={styles.image} aria-hidden={true} />
        )}
      </div>
    )
  }

  if (data.isButtonImg) {
    return (
      <div className={styles.container}>
        <a
          className={styles.buttonContainerImg}
          href={href}
          target={target}
          rel={rel}
          onMouseOver={() => setHoverButton(true)}
          onMouseLeave={() => setHoverButton(false)}
          onFocus={() => setHoverButton(true)}
          onBlur={() => setHoverButton(false)}
        >
          <span className={!data.imgContent ? styles.buttonContent : styles.buttonContentImg}>
            {!data.imgContent ? null : data.imgContent.format === "svg" ? (
              <ReactSVG
                src={data.imgContent.url}
                className={styles.btnImage}
                aria-hidden={true}
                beforeInjection={svg => configureSVGAccessibility(svg, data.imgContent?.alt || "")}
              />
            ) : (
              <img src={data.imgContent.url} alt={data.imgContent.alt || ""} className={styles.btnImage} />
            )}
          </span>
        </a>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <a
        className={styles.buttonContainer}
        href={href}
        target={target}
        rel={rel}
        style={
          data.color?.hex && {
            backgroundColor: hoverButton ? data.hoverColor.hex : data.color.hex,
            border: hoverButton && `1px solid ${data.color.hex}`,
          }
        }
        onMouseOver={() => setHoverButton(true)}
        onMouseLeave={() => setHoverButton(false)}
        onFocus={() => setHoverButton(true)}
        onBlur={() => setHoverButton(false)}
      >
        <span className={styles.buttonContent}>
          {data.pictogram && displayPictogram(data.pictogram, data.color, data.hoverColor, hoverButton)}
          <div className={styles.textContent}>
            <div className={styles.title} style={{ color: hoverButton ? data.color?.hex : data.hoverColor?.hex }}>
              {data.title}
            </div>
          </div>
        </span>
      </a>
    </div>
  )
}

export const fragments = graphql`
  fragment ButtonFields on DatoCmsButton {
    __typename
    id
    title
    isButtonImg
    imgContent {
      alt
      format
      url
    }
    pictogram {
      alt
      format
      url
    }
    color {
      hex
    }
    hoverColor {
      hex
    }
    internalPageLink {
      ... on DatoCmsHome {
        __typename
      }
      ... on DatoCmsPage {
        __typename
        slug
      }
    }
    externalPageLink
  }
`
export default Button
